import { DEFER_SEARCH_TIMEOUT } from '@/utils/constants';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

type TableSearchInPlaceArgs<DataResponse, UiItem> = {
  searchKey: QueryKey;
  queryFunction: (term: string) => Promise<DataResponse>;
  mapper: (dataResponse: DataResponse) => UiItem[];
};

let searchTimeout: NodeJS.Timeout;

export function useTableSearchInPlace<DataResponse, UiItem>(
  endpointKey: string,
  searchTerm: string,
  args: TableSearchInPlaceArgs<DataResponse, UiItem>
) {
  const [term, setTerm] = useState(searchTerm);

  const searchBuffer = useRef<UiItem[] | undefined>();

  const { data, error, isFetching } = useQuery({
    queryKey: [endpointKey, { term }, ...args.searchKey],
    queryFn: () => args.queryFunction(term),
    enabled: term.trim() !== '',
  });

  const mappedResults = useMemo<UiItem[] | undefined>(() => {
    return data === undefined ? undefined : args.mapper(data);
  }, [data]);

  const searchResults = (() => {
    if (isFetching) return searchBuffer.current;
    if (searchTerm.trim()) return mappedResults;
    return undefined;
  })();
  searchBuffer.current = searchResults;

  useEffect(() => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      setTerm(searchTerm);
    }, DEFER_SEARCH_TIMEOUT);
  }, [searchTerm]);

  return {
    searchResults: searchResults,
    error,
    isLoading: isFetching,
  };
}
